import { useEffect, useState } from "react";
import { useSnapshot } from "valtio";
import Loader from "src/components/common/loader/loader";
import { engagementStore } from "src/modules/engagement/engagementStore";
import { workflowEvents } from "src/modules/engagement/workflow/workflowEvents";
import { AdGroupService } from "@services/engagements/adGroups/adGroupService";
import { AudienceDefinitionService } from "@services/engagements/launch/audienceDefinitionService";
import { EngagementService } from "@services/engagements/engagementService";
import { LaunchNetworkService } from "@services/engagements/launch/launchNetworkService";
import { fetchReportingNetworkConfigs } from "@services/referenceData/reportingNetworkConfigService";
import { AD_GROUP_NETWORKS, useFeatureFlag } from "@/shared/featureFlags/useFeatureFlag";
import type { AdGroupListItem, AdGroupNetworkChoice } from "@models/adGroup.model";
import AdGroupsList from "./AdGroupsList";
import AdGroupFormModal from "./AdGroupFormModal";
import AdGroupDetail from "./AdGroupDetail";
import { networkLooks as toNetworkLooks, type NetworkLooks } from "./NetworkTag";
import { useItemRoute } from "../../../workflow/useItemRoute";
import "./adGroups.scss";

// organizationId is the campaign's sub-organization, which the ad editor needs —
// it falls back to the client org only when the campaign has no sub-org linked.
type Campaign = { id: number; name: string; organizationId: number | null };

async function fetchAdGroupsAndCampaigns(engagementId: number) {
  const [adGroups, campaigns] = await Promise.all([
    new AdGroupService().listAdGroups(engagementId),
    new EngagementService().getEngagementLaunches(engagementId),
  ]);
  return {
    adGroups,
    campaigns: campaigns.map((c) => ({
      id: c.id,
      name: c.name,
      organizationId: c.organizations?.[0]?.id ?? null,
    })),
  };
}

export default function AdGroupsStep() {
  const { selectedEngagement } = useSnapshot(engagementStore);
  const engagementId = selectedEngagement?.id ?? 0;

  const [adGroups, setAdGroups] = useState<AdGroupListItem[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [openAdGroupId, setOpenAdGroupId] = useItemRoute();
  const [creatingForLaunchId, setCreatingForLaunchId] = useState<number | null>(null);
  const [isEditingAdGroup, setIsEditingAdGroup] = useState(false);
  const [audiencesForModal, setAudiencesForModal] = useState<{ id: number; name: string }[]>([]);
  const choosesNetworks = useFeatureFlag(AD_GROUP_NETWORKS);
  const [networkLooks, setNetworkLooks] = useState<NetworkLooks>({});
  const [campaignNetworks, setCampaignNetworks] = useState<string[]>([]);
  const [modalError, setModalError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!engagementId) return;
    let cancelled = false;
    setError(null);
    setIsLoading(true);
    fetchAdGroupsAndCampaigns(engagementId)
      .then(({ adGroups: loaded, campaigns: loadedCampaigns }) => {
        if (cancelled) return;
        setAdGroups(loaded);
        setCampaigns(loadedCampaigns);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Could not load ad groups.");
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [engagementId]);

  const reload = async () => {
    const { adGroups: loaded, campaigns: loadedCampaigns } =
      await fetchAdGroupsAndCampaigns(engagementId);
    setAdGroups(loaded);
    setCampaigns(loadedCampaigns);
  };

  // The save went through and the modal is closed, so a failed reload belongs on the page.
  const reloadOrShowError = () => reload().catch(() => setError("Could not load ad groups."));

  const openAdGroup = adGroups.find((g) => g.id === openAdGroupId) ?? null;
  const creatingCampaign = campaigns.find((c) => c.id === creatingForLaunchId) ?? null;
  const audienceModalLaunchId =
    creatingForLaunchId ?? (isEditingAdGroup ? openAdGroup?.launch_id ?? null : null);

  // Audiences belong to the engagement, so an ad group can target any of them —
  // the campaign it sits in does not narrow the choice.
  useEffect(() => {
    if (audienceModalLaunchId == null) {
      setAudiencesForModal([]);
      return;
    }
    let cancelled = false;
    new AudienceDefinitionService()
      .listAudiences(engagementId)
      .then((loaded) => {
        if (cancelled) return;
        setAudiencesForModal(loaded.map((a) => ({ id: a.id, name: a.name })));
      })
      .catch(() => {
        if (cancelled) return;
        setAudiencesForModal([]);
      });
    return () => {
      cancelled = true;
    };
  }, [audienceModalLaunchId, engagementId]);

  // Labels and icons for network codes. Cosmetic: without them the codes show as they are.
  useEffect(() => {
    if (!choosesNetworks) return;
    let cancelled = false;
    fetchReportingNetworkConfigs()
      .then((configs) => {
        if (!cancelled) setNetworkLooks(toNetworkLooks(configs));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [choosesNetworks]);

  // The networks the campaign has switched on: what following means, and all the form can pick.
  useEffect(() => {
    setCampaignNetworks([]);
    if (!choosesNetworks || audienceModalLaunchId == null) return;
    let cancelled = false;
    new LaunchNetworkService()
      .getConfigs(audienceModalLaunchId)
      .then((configs) => {
        if (cancelled) return;
        setCampaignNetworks(
          configs.filter((c) => c.is_enabled).map((c) => c.network.toLowerCase()).sort(),
        );
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [choosesNetworks, audienceModalLaunchId]);

  // The server's refusal is the useful message here -- it says which network and why.
  const refusal = (err: unknown, fallback: string) =>
    err instanceof Error && err.message ? err.message : fallback;

  const closeModal = () => {
    setModalError(null);
    setCreatingForLaunchId(null);
    setIsEditingAdGroup(false);
  };

  const handleCreate = async (
    name: string,
    audienceDefinitionId: number | null,
    networks: AdGroupNetworkChoice | null,
  ) => {
    if (creatingForLaunchId == null) return;
    setIsSaving(true);
    setModalError(null);
    try {
      await new AdGroupService().createAdGroup(creatingForLaunchId, {
        name,
        audience_definition_id: audienceDefinitionId,
        ...networks,
      });
    } catch (err) {
      setModalError(refusal(err, "Could not create the ad group. Try again."));
      return;
    } finally {
      setIsSaving(false);
    }
    closeModal();
    workflowEvents.send_event("WORKFLOW_DATA_TOUCHED");
    await reloadOrShowError();
  };

  const handleEditSubmit = async (
    name: string,
    audienceDefinitionId: number | null,
    networks: AdGroupNetworkChoice | null,
  ) => {
    if (!openAdGroup) return;
    setIsSaving(true);
    setModalError(null);
    try {
      await new AdGroupService().updateAdGroup(openAdGroup.launch_id, openAdGroup.id, {
        name,
        audience_definition_id: audienceDefinitionId,
        ...networks,
      });
    } catch (err) {
      setModalError(refusal(err, "Could not save changes to the ad group. Try again."));
      return;
    } finally {
      setIsSaving(false);
    }
    closeModal();
    workflowEvents.send_event("WORKFLOW_DATA_TOUCHED");
    await reloadOrShowError();
  };

  if (isLoading) return <Loader />;

  return (
    <>
      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {openAdGroup ? (
        <AdGroupDetail
          adGroup={openAdGroup}
          organizationId={
            campaigns.find((c) => c.id === openAdGroup.launch_id)?.organizationId ?? null
          }
          onBack={() => setOpenAdGroupId(null)}
          onEdit={() => setIsEditingAdGroup(true)}
          onChanged={reload}
        />
      ) : (
        !error || campaigns.length > 0 || adGroups.length > 0 ? (
          <AdGroupsList
            adGroups={adGroups}
            campaigns={campaigns}
            networkLooks={networkLooks}
            isBusy={isSaving}
            onOpen={setOpenAdGroupId}
            onCreate={setCreatingForLaunchId}
          />
        ) : null
      )}

      {creatingCampaign && (
        <AdGroupFormModal
          mode="create"
          campaignName={creatingCampaign.name}
          audiences={audiencesForModal}
          campaignNetworks={campaignNetworks}
          networkLooks={networkLooks}
          error={modalError}
          isSaving={isSaving}
          onClose={closeModal}
          onSubmit={handleCreate}
        />
      )}

      {isEditingAdGroup && openAdGroup && (
        <AdGroupFormModal
          mode="edit"
          campaignName={openAdGroup.launch_name}
          initialName={openAdGroup.name}
          initialAudienceId={openAdGroup.audience_definition_id}
          initialFollowCampaign={openAdGroup.follow_campaign_networks}
          initialNetworks={openAdGroup.networks}
          audiences={audiencesForModal}
          campaignNetworks={campaignNetworks}
          networkLooks={networkLooks}
          error={modalError}
          isSaving={isSaving}
          onClose={closeModal}
          onSubmit={handleEditSubmit}
        />
      )}
    </>
  );
}
